import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FileText, Download, Filter } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };
const fmtRp = (n) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n || 0);
const fmtNum = (n) => Number(n || 0).toLocaleString('id-ID');

const PERIODS = [
  { id: 'harian', label: 'Hari Ini' },
  { id: 'mingguan', label: 'Minggu Ini' },
  { id: 'bulanan', label: 'Bulan Ini' },
  { id: 'ramadan', label: 'Ramadan' },
];

export default function ZakatLaporanPane({ mosqueId, profile }) {
  const [period, setPeriod] = useState('bulanan');
  const [dari, setDari] = useState('');
  const [sampai, setSampai] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const loadReport = async (p = period, d = dari, s = sampai) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/zakat-ramadan/laporan`, {
        ...cfg, params: { period: p, dari: d || undefined, sampai: s || undefined, limit: 100 }
      });
      setReport(data);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { loadReport(); }, [mosqueId]);

  const handleDownloadExcel = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API_URL}/api/zakat-ramadan/laporan/export-excel`, {
        ...cfg, params: { period, dari: dari || undefined, sampai: sampai || undefined },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `laporan-zakat-ramadan.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Gagal mengunduh Excel');
    } finally { setDownloading(false); }
  };

  const handlePrintPDF = () => {
    const stats = report?.stats;
    const win = window.open('', '_blank', 'width=800,height=900');
    const rows = (report?.transactions || []).map((txn, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${txn.nomor_transaksi}</td>
        <td>${txn.tanggal}</td>
        <td>${txn.nama_kk}</td>
        <td style="text-align:center">${txn.jumlah_jiwa}</td>
        <td>${txn.jenis_pembayaran === 'beras' ? 'Beras' : 'Uang'}</td>
        <td style="text-align:right">${txn.jenis_pembayaran === 'beras' ? `${txn.total_zakat_beras} kg` : fmtRp(txn.total_zakat_uang)}</td>
        <td style="text-align:right">${txn.has_infak ? fmtRp(txn.nominal_infak) : '-'}</td>
      </tr>`).join('');
    win.document.write(`<html><head><title>Laporan Zakat</title>
      <style>
        body{font-family:Arial;font-size:11px;margin:20px}
        h2{color:#065F46;margin-bottom:4px}
        .stats{display:flex;gap:16px;background:#f0fdf4;padding:12px;border-radius:8px;margin:12px 0}
        .stat{text-align:center}.stat-val{font-size:16px;font-weight:bold;color:#065F46}.stat-lbl{color:#6b7280;font-size:10px}
        table{width:100%;border-collapse:collapse;margin-top:12px}
        th{background:#065F46;color:white;padding:6px 8px;text-align:left}
        td{padding:5px 8px;border-bottom:1px solid #e5e7eb}
        @media print{.no-print{display:none}}
      </style></head><body>
      <h2>LAPORAN ZAKAT FITRAH RAMADAN</h2>
      <p>${profile?.entity_name || ''} &mdash; Periode: ${period.toUpperCase()}</p>
      <div class="stats">
        <div class="stat"><div class="stat-val">${fmtNum(stats?.total_muzakki)}</div><div class="stat-lbl">Muzakki</div></div>
        <div class="stat"><div class="stat-val">${fmtNum(stats?.total_jiwa)}</div><div class="stat-lbl">Jiwa</div></div>
        <div class="stat"><div class="stat-val">${fmtNum(stats?.total_beras_kg)} kg</div><div class="stat-lbl">Zakat Beras</div></div>
        <div class="stat"><div class="stat-val">${fmtRp(stats?.total_uang_rp)}</div><div class="stat-lbl">Zakat Uang</div></div>
        <div class="stat"><div class="stat-val">${fmtRp(stats?.total_infak_rp)}</div><div class="stat-lbl">Infak</div></div>
      </div>
      <table><thead><tr><th>No</th><th>Nomor</th><th>Tanggal</th><th>Muzakki</th><th>Jiwa</th><th>Jenis</th><th>Zakat</th><th>Infak</th></tr></thead>
      <tbody>${rows}</tbody></table>
      <script>window.print()</script></body></html>`);
    win.document.close();
  };

  const stats = report?.stats;

  return (
    <div className="space-y-5" data-testid="zakat-laporan">
      {/* Filters */}
      <div className="bg-white rounded-2xl border border-stone-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-emerald-600" />
          <span className="text-sm font-semibold text-stone-700">Filter Laporan</span>
        </div>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-stone-500 mb-1.5">Periode</label>
            <div className="flex gap-1.5 flex-wrap">
              {PERIODS.map(p => (
                <button key={p.id} onClick={() => { setPeriod(p.id); loadReport(p.id, dari, sampai); }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${period === p.id ? 'bg-emerald-700 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs text-stone-500 mb-1.5">Dari Tanggal</label>
            <input type="date" value={dari} onChange={e => setDari(e.target.value)}
              className="px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-emerald-500 outline-none" />
          </div>
          <div>
            <label className="block text-xs text-stone-500 mb-1.5">Sampai Tanggal</label>
            <input type="date" value={sampai} onChange={e => setSampai(e.target.value)}
              className="px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-emerald-500 outline-none" />
          </div>
          <button onClick={() => loadReport(period, dari, sampai)} disabled={loading}
            className="px-4 py-2 bg-emerald-700 text-white rounded-xl text-sm font-medium disabled:opacity-60">
            {loading ? 'Memuat...' : 'Tampilkan'}
          </button>
        </div>
      </div>

      {/* Stats summary */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Total Muzakki', value: fmtNum(stats.total_muzakki), unit: 'KK' },
            { label: 'Total Jiwa', value: fmtNum(stats.total_jiwa), unit: 'jiwa' },
            { label: 'Zakat Beras', value: `${fmtNum(stats.total_beras_kg)} kg`, unit: 'beras' },
            { label: 'Zakat Uang', value: fmtRp(stats.total_uang_rp), unit: 'rupiah' },
            { label: 'Total Infak', value: fmtRp(stats.total_infak_rp), unit: 'rupiah' },
          ].map(({ label, value, unit }) => (
            <div key={label} className="bg-white rounded-xl border border-stone-200 p-4 text-center">
              <p className="text-xs text-stone-500 mb-1">{label}</p>
              <p className="font-bold text-stone-800 text-sm">{value}</p>
              <p className="text-xs text-stone-400">{unit}</p>
            </div>
          ))}
        </div>
      )}

      {/* Export buttons */}
      {report && (
        <div className="flex gap-3">
          <button onClick={handlePrintPDF}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-stone-200 text-sm text-stone-700 hover:bg-stone-50 transition-colors">
            <FileText className="w-4 h-4 text-red-500" /> Export PDF (Print)
          </button>
          <button onClick={handleDownloadExcel} disabled={downloading}
            className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-60">
            <Download className="w-4 h-4" /> {downloading ? 'Mengunduh...' : 'Export Excel'}
          </button>
        </div>
      )}

      {/* Transaction table */}
      {report?.transactions?.length > 0 && (
        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-stone-100 text-sm font-semibold text-stone-700">
            Detail Transaksi ({report.total})
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-xs text-stone-500">
                <tr>
                  <th className="px-4 py-3 text-left">No. Transaksi</th>
                  <th className="px-4 py-3 text-left">Tanggal</th>
                  <th className="px-4 py-3 text-left">Muzakki</th>
                  <th className="px-4 py-3 text-center">Jiwa</th>
                  <th className="px-4 py-3 text-left">Jenis</th>
                  <th className="px-4 py-3 text-right">Zakat</th>
                  <th className="px-4 py-3 text-right">Infak</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {report.transactions.map(row => (
                  <tr key={row.id} className="hover:bg-stone-50">
                    <td className="px-4 py-3 font-mono text-xs text-stone-500">{row.nomor_transaksi}</td>
                    <td className="px-4 py-3 text-stone-600">{row.tanggal}</td>
                    <td className="px-4 py-3 font-medium text-stone-800">{row.nama_kk}</td>
                    <td className="px-4 py-3 text-center text-emerald-700 font-semibold">{row.jumlah_jiwa}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${row.jenis_pembayaran === 'beras' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                        {row.jenis_pembayaran === 'beras' ? 'Beras' : 'Uang'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold">
                      {row.jenis_pembayaran === 'beras' ? `${row.total_zakat_beras} kg` : fmtRp(row.total_zakat_uang)}
                    </td>
                    <td className="px-4 py-3 text-right text-rose-500">
                      {row.has_infak ? fmtRp(row.nominal_infak) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
